import { useEffect, useState } from "react";
import {
  Add20Regular,
  Checkmark20Regular,
  Delete20Regular,
  Dismiss20Regular,
  Edit20Regular,
} from "@fluentui/react-icons";
import {
  ApiError,
  createCompany,
  deleteCompany,
  getCompanies,
  updateCompany,
  type Company,
} from "../companies/companiesApi";

/**
 * Empresas do grupo. É esta lista que separa as folhas de faturas: cada documento importado é
 * encaminhado pela empresa cujo NIF consta dele como adquirente, pelo que o NIF tem de estar certo.
 */
export default function CompaniesSettingsTab() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newTaxId, setNewTaxId] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingTaxId, setEditingTaxId] = useState("");

  const load = async () => {
    try {
      setCompanies(await getCompanies());
      setError(null);
    } catch {
      setError("Não foi possível carregar as empresas.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showError = (err: unknown, fallback: string) => {
    // O servidor devolve ProblemDetails; a mensagem legível vale mais do que o JSON cru.
    if (err instanceof ApiError && err.message) {
      try {
        const parsed = JSON.parse(err.message);
        setError(parsed?.detail || parsed?.title || fallback);
        return;
      } catch {
        setError(err.message);
        return;
      }
    }
    setError(fallback);
  };

  const handleAdd = async () => {
    const name = newName.trim();
    const taxId = newTaxId.trim();
    if (!name || !taxId || isBusy) return;

    setIsBusy(true);
    setError(null);
    try {
      await createCompany({ name, taxId });
      setNewName("");
      setNewTaxId("");
      await load();
    } catch (err) {
      showError(err, "Não foi possível criar a empresa.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleSaveEdit = async (company: Company) => {
    const name = editingName.trim();
    const taxId = editingTaxId.trim();
    if (!name || !taxId || isBusy) return;

    setIsBusy(true);
    setError(null);
    try {
      await updateCompany(company.id, { name, taxId, isDefault: company.isDefault });
      setEditingId(null);
      await load();
    } catch (err) {
      showError(err, "Não foi possível guardar a empresa.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleMakeDefault = async (company: Company) => {
    if (isBusy || company.isDefault) return;

    setIsBusy(true);
    setError(null);
    try {
      await updateCompany(company.id, {
        name: company.name,
        taxId: company.taxId,
        isDefault: true,
      });
      await load();
    } catch (err) {
      showError(err, "Não foi possível definir a empresa por omissão.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (company: Company) => {
    if (isBusy) return;
    if (
      !window.confirm(
        `Eliminar a empresa "${company.name}"? As faturas continuam onde estão.`,
      )
    ) {
      return;
    }

    setIsBusy(true);
    setError(null);
    try {
      await deleteCompany(company.id);
      await load();
    } catch (err) {
      showError(err, "Não foi possível eliminar a empresa.");
    } finally {
      setIsBusy(false);
    }
  };

  if (isLoading) {
    return <p className="text-secondary-text">A carregar...</p>;
  }

  return (
    <div>
      <div className="mb-6">
        <h3
          className="text-foreground mb-1"
          style={{
            fontSize: "var(--text-h4)",
            fontWeight: "var(--font-weight-semi-bold)",
          }}
        >
          Empresas
        </h3>
        <p
          className="text-secondary-text"
          style={{ fontSize: "var(--text-caption)" }}
        >
          As faturas importadas são encaminhadas para a empresa cujo NIF consta do documento
          como adquirente. Sem correspondência, ficam na empresa por omissão. Uma empresa com
          faturas lançadas não pode ser eliminada.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <input
          type="text"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder="Nome da empresa (ex.: Semear Destreza, Lda)"
          className="flex-1 px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{
            borderRadius: "var(--radius-button)",
            fontSize: "var(--text-base)",
          }}
        />
        <input
          type="text"
          value={newTaxId}
          onChange={(event) => setNewTaxId(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleAdd();
          }}
          placeholder="NIF"
          className="w-40 px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{
            borderRadius: "var(--radius-button)",
            fontSize: "var(--text-base)",
          }}
        />
        <button
          onClick={handleAdd}
          disabled={isBusy || !newName.trim() || !newTaxId.trim()}
          className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            borderRadius: "var(--radius-button)",
            fontSize: "var(--text-caption)",
          }}
        >
          <Add20Regular className="w-4 h-4" />
          Adicionar
        </button>
      </div>

      {error && (
        <div
          className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      <div
        className="border border-border rounded"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full">
          <thead>
            <tr
              className="border-b border-border bg-secondary/50"
              style={{ fontSize: "var(--text-caption)" }}
            >
              <th
                className="text-left p-4 text-foreground"
                style={{ fontWeight: "var(--font-weight-semi-bold)" }}
              >
                Nome
              </th>
              <th
                className="text-left p-4 text-foreground"
                style={{ fontWeight: "var(--font-weight-semi-bold)", width: "160px" }}
              >
                NIF
              </th>
              <th
                className="text-left p-4 text-foreground"
                style={{ fontWeight: "var(--font-weight-semi-bold)", width: "140px" }}
              >
                Faturas
              </th>
              <th
                className="text-right p-4 text-foreground"
                style={{
                  fontWeight: "var(--font-weight-semi-bold)",
                  width: "220px",
                }}
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => {
              const isEditing = editingId === company.id;

              return (
                <tr
                  key={company.id}
                  className="border-b border-border hover:bg-secondary/30 transition-colors last:border-b-0"
                >
                  <td className="p-4">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        style={{ borderRadius: "var(--radius-button)" }}
                        autoFocus
                      />
                    ) : (
                      <span className="text-foreground inline-flex items-center gap-2">
                        {company.name}
                        {company.isDefault && (
                          <span
                            className="text-xs px-2 py-0.5"
                            style={{
                              borderRadius: "var(--radius-button)",
                              backgroundColor: "var(--accent)",
                              color: "var(--primary)",
                            }}
                          >
                            por omissão
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingTaxId}
                        onChange={(event) => setEditingTaxId(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") handleSaveEdit(company);
                          if (event.key === "Escape") setEditingId(null);
                        }}
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        style={{ borderRadius: "var(--radius-button)" }}
                      />
                    ) : (
                      <span className="text-secondary-text">{company.taxId}</span>
                    )}
                  </td>
                  <td className="p-4 text-secondary-text">
                    {company.financialEntriesCount}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleSaveEdit(company)}
                            disabled={isBusy}
                            className="p-2 rounded hover:bg-accent/30 disabled:opacity-50"
                            style={{ color: "var(--primary)" }}
                            title="Guardar"
                          >
                            <Checkmark20Regular className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-2 rounded hover:bg-accent/30 text-secondary-text"
                            title="Cancelar"
                          >
                            <Dismiss20Regular className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          {!company.isDefault && (
                            <button
                              onClick={() => handleMakeDefault(company)}
                              disabled={isBusy}
                              className="px-2 py-1 text-xs rounded hover:bg-accent/30 disabled:opacity-50 text-secondary-text"
                              title="Passa a receber as faturas que não identificam empresa"
                            >
                              Tornar por omissão
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setEditingId(company.id);
                              setEditingName(company.name);
                              setEditingTaxId(company.taxId);
                              setError(null);
                            }}
                            className="p-2 rounded hover:bg-accent/30 text-secondary-text"
                            title="Editar"
                          >
                            <Edit20Regular className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(company)}
                            disabled={isBusy}
                            className="p-2 rounded hover:bg-accent/30 disabled:opacity-50"
                            style={{ color: "var(--destructive)" }}
                            title="Eliminar"
                          >
                            <Delete20Regular className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
